import React from "react";
import styles from "../../../FlowPage/components/Task/TaskContainer.module.scss";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRightFromLine, faArrowRightFromBracket, faArrowLeftFromBracket, faArrowLeftFromLine } from "@fortawesome/pro-light-svg-icons";

import {useActiveProjectCalendarsSelector, useCpmMapSelector} from "../../../../../../store/selectors/project.selectors";
import {convertIndexToSeconds} from "../../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import moment from "moment";
import {useDispatch} from "react-redux";
import {useAllTaskListSelector} from "../../../../../../store/selectors/task/task.selectors";
import * as taskActions from "../../../../../../store/actions/task.actions";
import * as searchActions from "../../../../../../store/actions/search.actions";
import {DateIndexationUtils} from "../../../../../../utils/dateIndexation.utils";
import {TaskType} from "../../../../../../models/task-type";
import {CpmLinkModel} from "../../../../../../models/responses/cpm-task.model";
import TaskStatusModel from "../../../../../../models/responses/task-status.model";
import {TaskPredType} from "../../../../../../models/task-pred-type.enum";


interface InjectedProps {
    link: CpmLinkModel | any;
    index: number;
    predecessorToggle: boolean;
    navigateToTask: any
}

export const TaskLinkContainer: React.FC<InjectedProps> = React.memo(
    ({...props }) => {
        const { link, index, predecessorToggle, navigateToTask } = props;
        const cpmMap = useCpmMapSelector()
        const task: any = cpmMap.get(link.task_id)
        const predTask: any = cpmMap.get(link.pred_task_id)
        const calendarMap = useActiveProjectCalendarsSelector();
        const workingHours = calendarMap.get(`whd:${task.cal_id}`);
        const dispatch = useDispatch();
        const allTasks = useAllTaskListSelector()

        const linkConverter = () => {
            switch (link.pred_type) {
                case TaskPredType.FS:
                    return "Finish to Start";
                case TaskPredType.SS:
                    return "Start to Start";
                case TaskPredType.FF:
                    return "Finish to Finish";
                case TaskPredType.SF:
                    return "Start to Finish";
            }
        }

        const relevantDatePicker = () => {
            if (predecessorToggle) {
                switch (link.pred_type) {
                    case TaskPredType.FS:
                        return new Date(convertIndexToSeconds(predTask.ef + link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.SS:
                        return new Date(convertIndexToSeconds(predTask.es + link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.FF:
                        return new Date(convertIndexToSeconds(predTask.ef + link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.SF:
                        return new Date(convertIndexToSeconds(predTask.es + link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                }
            } else {
                let lateralIndex: number;
                switch (link.pred_type) {
                    case TaskPredType.FS:
                        lateralIndex = DateIndexationUtils.getLateralIndex(task.es, task.cal_id, predTask.cal_id, 0, calendarMap);
                        return new Date(convertIndexToSeconds(lateralIndex - link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.SS:
                        lateralIndex = DateIndexationUtils.getLateralIndex(task.es, task.cal_id, predTask.cal_id, 0, calendarMap);
                        return new Date(convertIndexToSeconds(lateralIndex - link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.FF:
                        lateralIndex = DateIndexationUtils.getLateralIndex(task.ef, task.cal_id, predTask.cal_id, 0, calendarMap);
                        return new Date(convertIndexToSeconds(lateralIndex - link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                    case TaskPredType.SF:
                        lateralIndex = DateIndexationUtils.getLateralIndex(task.es, task.cal_id, predTask.cal_id, 0, calendarMap);
                        return new Date(convertIndexToSeconds(lateralIndex - link.lag_hr_cnt, predTask.cal_id, calendarMap) * 1000);
                }
            }
        }

        const handleStatusTypes = {
            [TaskStatusModel.COMPLETE]: 'completed',
            [TaskStatusModel.IN_PROGRESS]: 'in progress',
            [TaskStatusModel.NOT_STARTED]: 'not started',
            [TaskStatusModel.SUSPENDED]: 'in progress',
            [TaskStatusModel.BLOCK]: 'not started',
            [TaskStatusModel.DECLARED_COMPLETE]: 'completed',
        }

        const predStatus = () => {
            if (handleStatusTypes[predTask.status_code] === 'completed') {
                return styles.LineGreen
            }
            switch (predTask.predStatus) {
                case 1:
                    return styles.LineYellow;
                case 2:
                    return styles.LineRed;
                case 3:
                    return styles.LineRed;
                case 4:
                    return styles.LineGreen
            }
        }

        const predStatusColours = () => {
            if (handleStatusTypes[predTask.status_code] === 'completed') {
                return 'rgb(15, 157, 88, 0.2)'
            }
            switch (predTask.predStatus) {
                case 1:
                    return 'rgb(244, 180, 0, 0.2)'
                case 2:
                    return 'rgb(217, 0, 27, 0.2)'
                case 3:
                    return 'rgb(217, 0, 27, 0.2)'
                case 4:
                    return 'rgb(15, 157, 88, 0.2)'
            }
        }

        const handleStatusText = {
            [TaskStatusModel.COMPLETE]: 'Complete',
            [TaskStatusModel.IN_PROGRESS]: 'In Progress',
            [TaskStatusModel.NOT_STARTED]: 'Not Started',
            [TaskStatusModel.SUSPENDED]: 'Suspended',
            [TaskStatusModel.BLOCK]: 'Blocked',
            [TaskStatusModel.DECLARED_COMPLETE]: 'Declared Complete (awaiting approval)',
        }

        const getDateText = () => {
            if (predecessorToggle) {
                switch (link.pred_type) {
                    case "FS":
                        return handleStatusTypes[predTask.status_code] === 'completed' ? 'Actual Finish' : 'Early Finish';
                    case "SS":
                        return handleStatusTypes[predTask.status_code] !== 'not started' ? 'Actual Start' : 'Early Start';
                    case "FF":
                        return handleStatusTypes[predTask.status_code] === 'completed' ? 'Actual Finish' : 'Early Finish';
                    case "SF":
                        return handleStatusTypes[predTask.status_code] !== 'not started' ? 'Actual Start' : 'Early Start';
                    default:
                        return 'Early Finish';
                }
            }
        }

        const getLinkTag = () => {
            if (link.ad === task.ad && task.float <= 2) {
                return <div className={styles.CriticalTag}>Critical</div>
            } else if (link.ad === task.ad) {
                return <div className={styles.DrivingTag}>Driving</div>
            }
        }

        return (
            <div className={styles.LinkContainer} style={{cursor: "pointer"}}
                 onClick={(e) => {
                     e.stopPropagation();
                     const taskForActive = allTasks.get(
                         predecessorToggle ? link.pred_task_id : link.task_id);
                     dispatch(taskActions.Actions.setActiveTask(taskForActive ? taskForActive : null));
                     if (taskForActive && (taskForActive.task_type === TaskType.TT_MILE || taskForActive.task_type === TaskType.TT_FIN_MILE)) {
                         dispatch(searchActions.SearchActions.setIncludeMilestones(true));
                     }
                     navigateToTask()
                 }}
            >
                <div className={styles.LinkIndex}>
                    <div className={styles.LinkIndexNumber}>{index}</div>
                    <div className={styles.LinkTypeContainer}
                         style={{backgroundColor: predStatusColours()}}
                    >
                        <h4>{link.pred_type}</h4>
                    </div>
                </div>
                <div className={styles.IconWrap} style={{marginLeft: "10px"}}>
                    <>
                        <FontAwesomeIcon icon={link.pred_type === "FS" ?
                            faArrowRightFromLine :
                            link.pred_type === "SS" ?
                                faArrowRightFromBracket :
                                link.pred_type === "FF" ?
                                    faArrowLeftFromBracket : faArrowLeftFromLine
                        }/>
                    </>
                    <div className={styles.ProgressBarWrap}>
                        <div className={styles.ProgressBar}>
                            <div
                                style={{height: "100%"}}
                                className={predStatus()}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.LinkDescription}>
                    <div className="d-flex">
                        <h4>{predecessorToggle ? predTask.task_name: task.task_name}</h4>
                    </div>
                    <h5>
                        {handleStatusText[predecessorToggle ? predTask.status_code : task.status_code]}
                    </h5>
                    <div className={styles.Description}>
                        <h4>{linkConverter()}{` ${link.lag_hr_cnt < 0 ? ' - ': ' + '}${(link.lag_hr_cnt / workingHours) / 2} days`}</h4>
                    </div>
                </div>
                <div className={styles.rightFilters}>
                    {getLinkTag()}
                    <div className={styles.LinkDateContainer}>
                        <p className={styles.LinkDate}>{moment(relevantDatePicker()).format('DD-MMM-YY')}</p>
                        <p className={styles.LinkDateText}>{getDateText()}</p>
                    </div>
                </div>
            </div>
        );
    }
);
